<template>
  <div class="mt-6">
    <p class="text-sm text-black mb-2">Zone d'upload :</p>

    <!-- Zone de drop -->
    <div
      class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center transition-colors cursor-pointer"
      :class="isDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300'"
      @dragover.prevent="isDragOver = true"
      @dragleave.prevent="isDragOver = false"
      @drop.prevent="handleFileDrop"
      @click="triggerFileInput"
    >
      <input
        ref="fileInput"
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        class="hidden"
        @change="handleFileSelect"
      />

      <div v-if="!isUploading" class="space-y-3">
        <i class="fas fa-cloud-upload-alt text-black text-3xl"></i>
        <div>
          <p class="text-sm font-medium text-black">
            Glisser la facture dans cette zone ou cliquez pour sélectionner un fichier
          </p>
        </div>
        <p class="text-xs text-black mt-1">
          Formats acceptés : PDF, JPG, JPEG, PNG • Taille maximale : 10 MB
        </p>
      </div>

      <div v-else class="space-y-3">
        <i class="fas fa-spinner fa-spin text-blue-500 text-3xl"></i>
        <div>
          <p class="text-sm font-medium text-black">Upload en cours...</p>
          <p class="text-xs text-black">
            {{ currentUploadingFile?.name }}
          </p>
        </div>
      </div>
    </div>

    <!-- Liste des factures uploadées -->
    <div v-if="uploadedFiles.length > 0" class="mt-4">
      <table class="min-w-full border border-gray-300 rounded-md">
        <thead class="bg-gray-50">
          <tr>
            <th
              class="px-4 py-2 text-left text-sm font-medium text-black border-b"
            >
              Facture existante
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(file, index) in uploadedFiles"
            :key="index"
            class="border-b hover:bg-gray-50"
          >
            <td class="px-4 py-2">
              <div class="flex items-center justify-between">
                <a
                  :href="file.url"
                  target="_blank"
                  class="text-sm text-blue-600 hover:underline flex items-center gap-2"
                >
                  <i class="fas fa-file-pdf text-red-500"></i>
                  <span class="text-black">{{ file.filename }}</span>
                </a>
                <button
                  @click.stop="handleDeleteInvoice(file.filename)"
                  :disabled="isDeleting"
                  class="px-2 py-1 bg-red-100 text-red-600 text-xs rounded hover:bg-red-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal de confirmation de suppression -->
    <ModalComp
      :is-visible="showDeleteModal"
      title="Supprimer la facture"
      content="Voulez-vous vraiment supprimer cette facture ?"
      cancel-text="Annuler"
      confirm-text="Supprimer"
      size="sm"
      @update:is-visible="showDeleteModal = $event"
      @confirm="confirmDeleteInvoice"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import {
  uploadInvoice,
  getUploadedInvoices,
  deleteInvoice,
} from "@/api/orders";
import { useNotification } from "@/composables/useNotification";
import ModalComp from "@/components/ui/ModalComp.vue";

// Props
interface Props {
  orderId: number;
  orderGroupId: number;
  orderDate: string;
  invoiceId?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  invoiceUploaded: [action: "upload" | "delete"];
  invoicesCountChanged: [count: number];
}>();

// État
const isDragOver = ref(false);
const fileInput = ref<HTMLInputElement>();
const isUploading = ref(false);
const currentUploadingFile = ref<File | null>(null);
const uploadedFiles = ref<Array<{ filename: string; url: string }>>([]);
const isDeleting = ref(false);
const showDeleteModal = ref(false);
const invoiceToDelete = ref<string>("");

const { success, error: showError } = useNotification();

// Charger les factures existantes au montage
const loadExistingInvoices = async () => {
  try {
    const invoices = await getUploadedInvoices(props.orderId);
    uploadedFiles.value = invoices;
    // Émettre le nombre de factures après chargement
    emit("invoicesCountChanged", invoices.length);
  } catch (error) {
    emit("invoicesCountChanged", 0);
  }
};

// Méthodes
const triggerFileInput = () => {
  fileInput.value?.click();
};

const handleFileSelect = async (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files[0]) {
    await uploadFile(target.files[0]);
    // Réinitialiser l'input pour permettre le même fichier
    if (fileInput.value) {
      fileInput.value.value = "";
    }
  }
};

const handleFileDrop = async (event: DragEvent) => {
  isDragOver.value = false;
  if (event.dataTransfer?.files && event.dataTransfer.files[0]) {
    await uploadFile(event.dataTransfer.files[0]);
  }
};

// Validation du fichier avant upload
const validateFile = (file: File): { valid: boolean; error?: string } => {
  // Vérifier le format
  const allowedExts = [".pdf", ".jpg", ".jpeg", ".png"];
  const fileName = file.name.toLowerCase();
  const ext = fileName.substring(fileName.lastIndexOf("."));

  if (!allowedExts.includes(ext)) {
    return {
      valid: false,
      error: `Format de fichier non autorisé. Formats acceptés : PDF, JPG, JPEG, PNG. Votre fichier : ${ext.toUpperCase()}`,
    };
  }

  // Vérifier la taille (10 MB max)
  const maxSize = 10 * 1024 * 1024; // 10 MB
  const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);

  if (file.size > maxSize) {
    return {
      valid: false,
      error: `Fichier trop volumineux. Taille maximale autorisée : 10 MB. Taille de votre fichier : ${fileSizeMB} MB`,
    };
  }

  return { valid: true };
};

const uploadFile = async (file: File) => {
  if (isUploading.value) return;

  // Validation avant upload
  const validation = validateFile(file);
  if (!validation.valid) {
    showError(validation.error || "Fichier invalide");
    return;
  }

  isUploading.value = true;
  currentUploadingFile.value = file;

  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("idCommande", String(props.orderId));

    const result = await uploadInvoice(formData);

    if (result.success) {
      success("Facture uploadée avec succès");
      emit("invoiceUploaded", "upload");
      // Émettre un événement personnalisé pour les composants de labels
      window.dispatchEvent(
        new CustomEvent("invoiceUploaded", {
          detail: { orderId: props.orderId },
        }),
      );
      // Recharger la liste des factures
      await loadExistingInvoices();
    }
  } catch (err) {
    // Extraire le message d'erreur de manière plus explicite
    let errorMessage = "Erreur lors de l'upload de la facture";

    if (err instanceof Error) {
      const errorText = err.message;

      // Détecter les erreurs spécifiques
      if (
        errorText.includes("fileSize") ||
        errorText.includes("too large") ||
        errorText.includes("volumineux")
      ) {
        errorMessage =
          "Fichier trop volumineux. La taille maximale autorisée est de 10 MB.";
      } else if (
        errorText.includes("Type de fichier non autorisé") ||
        errorText.includes("format")
      ) {
        errorMessage =
          "Format de fichier non autorisé. Formats acceptés : PDF, JPG, JPEG, PNG.";
      } else if (errorText.includes("Aucun fichier fourni")) {
        errorMessage =
          "Aucun fichier n'a été sélectionné. Veuillez choisir un fichier à uploader.";
      } else if (errorText.includes("Commande introuvable")) {
        errorMessage =
          "Commande introuvable ou vous n'avez pas l'autorisation d'uploader une facture pour cette commande.";
      } else if (
        errorText.includes("HTTP: 413") ||
        errorText.includes("Request Entity Too Large")
      ) {
        errorMessage =
          "Fichier trop volumineux. La taille maximale autorisée est de 10 MB.";
      } else if (errorText.includes("HTTP: 400")) {
        // Extraire le message du backend si disponible
        const match = errorText.match(/HTTP: 400 - (.+)/);
        if (match && match[1]) {
          errorMessage = match[1];
        } else {
          errorMessage =
            "Erreur lors de l'upload. Veuillez vérifier le format et la taille de votre fichier.";
        }
      } else {
        // Utiliser le message d'erreur tel quel s'il est explicite
        errorMessage = errorText;
      }
    }

    showError(errorMessage);
  } finally {
    isUploading.value = false;
    currentUploadingFile.value = null;
  }
};

const handleDeleteInvoice = (filename: string) => {
  if (isDeleting.value) return;
  invoiceToDelete.value = filename;
  showDeleteModal.value = true;
};

const confirmDeleteInvoice = async () => {
  if (!invoiceToDelete.value) return;

  isDeleting.value = true;
  try {
    await deleteInvoice(props.orderId, invoiceToDelete.value);
    success("Facture supprimée avec succès");
    emit("invoiceUploaded", "delete");
    // Émettre un événement personnalisé pour les composants de labels
    window.dispatchEvent(
      new CustomEvent("invoiceDeleted", { detail: { orderId: props.orderId } }),
    );
    // Recharger la liste des factures
    await loadExistingInvoices();
  } catch (err) {
    showError(
      err instanceof Error
        ? err.message
        : "Erreur lors de la suppression de la facture",
    );
  } finally {
    isDeleting.value = false;
    invoiceToDelete.value = "";
  }
};

onMounted(() => {
  loadExistingInvoices();
});
</script>
